import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  StartupActivityTimelineItem,
  StartupInterestedInvestorItem,
  StartupUserActivityItem,
} from '../../types/startupDashboard';
import heartIcon from '../../assets/favorites_icon_(green heart).png';
import eyeIcon from '../../assets/eye.png';
import InterestedInvestorsPanel from './InterestedInvestorsPanel';
import StartupActivityTimelinePanel from './StartupActivityTimelinePanel';
import DashboardSectionTab from './DashboardSectionTab';
import { isRevealedTimelineInvestor } from '../../utils/revealedInvestorIdentity';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';

interface StartupDashboardContentProps {
  interestedInvestors: StartupInterestedInvestorItem[];
  activityTimeline: StartupActivityTimelineItem[];
  onResponseSubmitted?: (investorId: string) => void;
}

function coerceActivityDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object' && value !== null) {
    const maybe = value as { seconds?: number; _seconds?: number; toDate?: () => Date };
    if (typeof maybe.toDate === 'function') {
      const parsed = maybe.toDate();
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const seconds = maybe.seconds ?? maybe._seconds;
    if (typeof seconds === 'number') {
      const parsed = new Date(seconds * 1000);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  return null;
}

type MobileTab = 'interested' | 'timeline';

const StartupDashboardContent: React.FC<StartupDashboardContentProps> = ({
  interestedInvestors,
  activityTimeline,
  onResponseSubmitted,
}) => {
  const [mobileTab, setMobileTab] = useState<MobileTab>('interested');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { startupProfile } = useProfile();

  const userActivity = React.useMemo((): StartupUserActivityItem[] => {
    const items: StartupUserActivityItem[] = [];

    const joinedAt =
      coerceActivityDate(user?.createdAt) || coerceActivityDate(startupProfile?.createdAt);
    if (joinedAt) {
      items.push({
        id: 'user-joined-prairi',
        text: 'You joined PRAIRI',
        timestamp: joinedAt.toISOString(),
      });
    }

    if (startupProfile?.pitchDeckUrl?.trim()) {
      const deckAt =
        coerceActivityDate(startupProfile.pitchDeckUpdatedAt) ||
        coerceActivityDate(startupProfile.lastUpdatedAt) ||
        coerceActivityDate(startupProfile.updatedAt);
      if (deckAt) {
        items.push({
          id: 'user-updated-pitch-deck',
          text: 'You updated your pitch deck',
          timestamp: deckAt.toISOString(),
        });
      }
    }

    return items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [user?.createdAt, startupProfile]);

  useEffect(() => {
    if (searchParams.get('reset') !== 'true') return;
    const next = new URLSearchParams(searchParams);
    next.delete('reset');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Enrich timeline items with investor photos from interestedInvestors if available
  const enrichedActivityTimeline = React.useMemo(() => {
    const interestedByName = new Map(
      interestedInvestors
        .filter(inv => inv.investorName)
        .map(inv => [inv.investorName.toLowerCase(), inv])
    );
    const interestedById = new Map(
      interestedInvestors.filter(inv => inv.investorId).map(inv => [inv.investorId, inv])
    );

    return activityTimeline.map(item => {
      const match =
        (item.investorId ? interestedById.get(item.investorId) : undefined) ||
        (item.investorName ? interestedByName.get(item.investorName.toLowerCase()) : undefined);

      const photo = item.profilePictureUrl || match?.profilePictureUrl || undefined;

      return {
        ...item,
        investorId: item.investorName ? item.investorId || match?.investorId : item.investorId,
        profilePictureUrl: photo,
        city: item.city || match?.city,
        state: item.state || match?.state,
        investorName: item.investorName,
      };
    });
  }, [activityTimeline, interestedInvestors]);

  const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(null);
  const [selectionTimestamp, setSelectionTimestamp] = useState<number>(0);
  const [selectedTimelineInvestor, setSelectedTimelineInvestor] =
    useState<StartupInterestedInvestorItem | null>(null);

  const displayedInvestors = React.useMemo(() => {
    const list = [...interestedInvestors];
    if (selectedTimelineInvestor) {
      const existsIdx = list.findIndex(
        inv =>
          (selectedTimelineInvestor.investorId &&
            inv.investorId === selectedTimelineInvestor.investorId) ||
          (selectedTimelineInvestor.id && inv.id === selectedTimelineInvestor.id) ||
          (selectedTimelineInvestor.investorName &&
            inv.investorName?.trim().toLowerCase() ===
              selectedTimelineInvestor.investorName?.trim().toLowerCase())
      );
      if (existsIdx >= 0) {
        list.splice(existsIdx, 1);
      }
      list.unshift(selectedTimelineInvestor);
    }
    return list;
  }, [interestedInvestors, selectedTimelineInvestor]);

  const handleSelectInvestor = (investorId: string, investorName?: string) => {
    const name = investorName?.trim() ?? '';
    if (!isRevealedTimelineInvestor({ investorId, investorName: name })) {
      return;
    }

    const targetName = name.toLowerCase();
    const matched =
      (investorId
        ? interestedInvestors.find(inv => inv.investorId === investorId || inv.id === investorId)
        : undefined) ||
      (targetName
        ? interestedInvestors.find(
            inv =>
              inv.investorName &&
              (inv.investorName.trim().toLowerCase() === targetName ||
                targetName.includes(inv.investorName.trim().toLowerCase()) ||
                inv.investorName.trim().toLowerCase().includes(targetName))
          )
        : undefined);

    const timelineMatch = enrichedActivityTimeline.find(
      t =>
        (investorId && t.investorId === investorId) ||
        (targetName && t.investorName?.trim().toLowerCase() === targetName) ||
        (t.investorName &&
          targetName &&
          (t.investorName.trim().toLowerCase().includes(targetName) ||
            targetName.includes(t.investorName.trim().toLowerCase())))
    );

    const resolvedName =
      investorName || timelineMatch?.investorName || matched?.investorName || 'Investor';
    const resolvedId =
      matched?.investorId || matched?.id || investorId || timelineMatch?.investorId || '';
    if (!resolvedId) return;

    const targetItem: StartupInterestedInvestorItem = {
      ...(matched || {}),
      id: resolvedId,
      investorId: resolvedId,
      investorName: resolvedName,
      companyName: matched?.companyName || '',
      country: matched?.country || timelineMatch?.country || '',
      profilePictureUrl: matched?.profilePictureUrl || timelineMatch?.profilePictureUrl,
      hasResponded: matched?.hasResponded ?? false,
      startupStatus: matched?.startupStatus || 'favorited',
      consentedAt: matched?.consentedAt || new Date().toISOString(),
      investorHasReachedOut: matched?.investorHasReachedOut || false,
      timestamp: matched?.timestamp || new Date().toISOString(),
    };

    setSelectedInvestorId(resolvedId);
    setSelectedTimelineInvestor(targetItem);
    setSelectionTimestamp(Date.now());
    setMobileTab('interested');
  };

  return (
    <div className="min-h-screen bg-[#1d1d1d] pb-24 lg:pb-0">
      {/* ── Mobile layout ─────────────────────────────────── */}
      <div className="lg:hidden">
        {/* Tab bar */}
        <div className="flex justify-center gap-6 px-4 pt-5 pb-4">
          <DashboardSectionTab
            label="Interested"
            count={displayedInvestors.length}
            variant="interested"
            iconSrc={heartIcon}
            iconAlt=""
            active={mobileTab === 'interested'}
            onClick={() => setMobileTab('interested')}
          />
          <DashboardSectionTab
            label="Views & Activity"
            count={enrichedActivityTimeline.length}
            iconSrc={eyeIcon}
            iconAlt=""
            active={mobileTab === 'timeline'}
            onClick={() => setMobileTab('timeline')}
          />
        </div>

        {/* Active panel */}
        <div className="px-4">
          {mobileTab === 'interested' ? (
            <InterestedInvestorsPanel
              investors={displayedInvestors}
              onResponseSubmitted={onResponseSubmitted}
              selectedInvestorId={selectedInvestorId}
              selectionTimestamp={selectionTimestamp}
              hideHeader
            />
          ) : (
            <StartupActivityTimelinePanel
              items={enrichedActivityTimeline}
              userActivity={userActivity}
              onSelectInvestor={(investorId, investorName) => {
                const matched = displayedInvestors.find(
                  inv => inv.investorId === investorId || inv.id === investorId
                );
                if (!matched?.hasResponded) return;
                handleSelectInvestor(investorId, investorName);
              }}
              hideHeader
            />
          )}
        </div>
      </div>

      {/* ── Desktop layout ────────────────────────────────── */}
      <div className="hidden lg:block overflow-visible bg-[#1d1d1d] pt-0 pb-0 w-full min-h-[calc(100vh-3.5rem)]">
        <div className="relative w-full min-h-[calc(100vh-3.5rem)] flex flex-col">
          <div className="flex flex-row w-full flex-1 items-stretch">
            {/* Left column: Interested Investors (widened further) */}
            <div className="w-[445px] lg:w-[470px] shrink-0 pl-4 lg:pl-5 pr-4 lg:pl-5 pt-4 lg:pt-6 pb-6">
              <InterestedInvestorsPanel
                investors={displayedInvestors}
                onResponseSubmitted={onResponseSubmitted}
                selectedInvestorId={selectedInvestorId}
                selectionTimestamp={selectionTimestamp}
              />
            </div>

            {/* Vertical black divider (12px / 0.75rem) extending seamlessly from top to the bottom horizontal divider */}
            <div className="w-3 bg-black shrink-0 self-stretch" aria-hidden="true" />

            {/* Right column: Activity feed */}
            <div className="flex-1 min-w-0 pl-6 lg:pl-8 pr-4 lg:pr-6 pt-4 lg:pt-6 pb-6">
              <StartupActivityTimelinePanel
                items={enrichedActivityTimeline}
                userActivity={userActivity}
                onSelectInvestor={handleSelectInvestor}
              />
            </div>
          </div>

          {/* Bottom horizontal black divider extending all the way to the left and right */}
          <div className="h-3 bg-black w-full shrink-0" aria-hidden="true" />
        </div>
      </div>

      {/* ── Sticky "View Your Profile" footer (mobile only) ── */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#1d1d1d]/95 backdrop-blur-sm border-t border-gray-800 px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => navigate('/profile/startup/view')}
          className="w-full rounded-xl border border-gray-600 py-3 text-sm font-semibold text-white hover:bg-white/5 transition-colors"
        >
          View Your Profile
        </button>
      </div>
    </div>
  );
};

export default StartupDashboardContent;
